import { useState } from "react";
import { Control, FieldPath, FieldValues } from "react-hook-form";
import { cn } from "../../../../lib/utils";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../ui/common/form";
import { Input } from "../../../ui/common/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../ui/common/select";

interface UnitOption {
  key?: string | number;
  value: string | number;
  label?: string;
  text?: string;
}

interface FormScientificUnitInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TUnitName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  control: Control<TFieldValues>;
  name: TName;
  label?: string | null;
  required?: boolean | string;
  placeholder?: string;
  errorMessage?: string;
  // Validation rules
  min?: number | { value: number; message: string };
  max?: number | { value: number; message: string };
  validate?: Record<string, (value: any) => boolean | string> | ((value: any) => boolean | string);
  // Optional unit support
  unitType?: string;
  unitOptions?: UnitOption[];
  unitName?: TUnitName;
  unitRequired?: boolean;
  unitErrorMessage?: string;
  disabled?: boolean;
  /**
   * Once the field is left, values whose fractional part needs more digits
   * than this switch to scientific notation for display; everything else
   * stays in plain decimal. Defaults to 2 (e.g. `0.25` stays plain,
   * `0.0000025` becomes `2.5e-6`).
   */
  decimalThreshold?: number;
}

/**
 * Sanitizes free-typed text down to the longest valid prefix of a decimal or
 * scientific-notation number: an optionally-signed mantissa (one leading
 * `-`, one `.`) optionally followed by `e`/`E` and an optionally-signed
 * integer exponent. The `e` is dropped until the mantissa has a digit to
 * attach it to, and the exponent never accepts a decimal point.
 */
const sanitizeScientificInput = (value: any): string => {
  const raw = String(value ?? "").replace(/[^0-9.eE+-]/g, "");

  const exponentMarkerIndex = raw.search(/[eE]/);
  const rawMantissa = exponentMarkerIndex === -1 ? raw : raw.slice(0, exponentMarkerIndex);
  const rawExponent =
    exponentMarkerIndex === -1 ? null : raw.slice(exponentMarkerIndex + 1).replace(/[eE]/g, "");

  const mantissaIsNegative = rawMantissa.startsWith("-");
  const [mantissaInt = "", ...mantissaDecimalParts] = rawMantissa.replace(/-/g, "").split(".");
  const mantissa =
    (mantissaIsNegative ? "-" : "") +
    (mantissaDecimalParts.length ? `${mantissaInt}.${mantissaDecimalParts.join("")}` : mantissaInt);

  if (rawExponent === null || !/\d/.test(mantissa)) {
    return mantissa;
  }

  const exponentIsNegative = rawExponent.startsWith("-");
  const exponentIsPositive = !exponentIsNegative && rawExponent.startsWith("+");
  const exponentDigits = rawExponent.replace(/[^0-9]/g, "");
  const exponentSign = exponentIsNegative ? "-" : exponentIsPositive ? "+" : "";

  return `${mantissa}e${exponentSign}${exponentDigits}`;
};

/**
 * Converts a complete, sanitized value (as produced by
 * `sanitizeScientificInput`) into plain decimal notation, so scientific
 * notation is only ever a typing shorthand — what gets saved to the form
 * (and shown back once the field is left) is always a normal number. Shifts
 * the decimal point through the digit string directly instead of going
 * through `Number`, so there's no float-precision loss for very small/large
 * exponents. A value with no `e` is returned unchanged.
 */
const toPlainDecimalString = (value: string): string => {
  const exponentMarkerIndex = value.search(/e/i);
  if (exponentMarkerIndex === -1) return value;

  const mantissa = value.slice(0, exponentMarkerIndex);
  const exponent = value.slice(exponentMarkerIndex + 1);

  const isNegative = mantissa.startsWith("-");
  const [intPart = "", fracPart = ""] = mantissa.replace("-", "").split(".");
  const digits = intPart + fracPart;
  const exponentValue = parseInt(exponent, 10);
  const pointPosition = intPart.length + exponentValue;

  let unsigned: string;
  if (pointPosition <= 0) {
    unsigned = `0.${"0".repeat(-pointPosition)}${digits}`;
  } else if (pointPosition >= digits.length) {
    unsigned = `${digits}${"0".repeat(pointPosition - digits.length)}`;
  } else {
    unsigned = `${digits.slice(0, pointPosition)}.${digits.slice(pointPosition)}`;
  }

  return (isNegative ? "-" : "") + unsigned;
};

// No digits typed yet ("", "-", ".", "-.") - treat as not-yet-entered.
const hasNoDigits = (value: any) => !/\d/.test(String(value ?? ""));

// Exponent marker present but its digits aren't typed yet ("5e", "5e-", "5e+").
const hasIncompleteExponent = (value: any) => /e[+-]?$/i.test(String(value ?? "").trim());

const isEmpty = (value: any) => hasNoDigits(value);
const isIncomplete = (value: any) => hasNoDigits(value) || hasIncompleteExponent(value);

/**
 * Inverse of `toPlainDecimalString`: renders a plain-decimal string in
 * scientific notation (one digit before the decimal point), using the same
 * digit-shifting string math so the exact value the user entered is
 * preserved with no rounding. A value that's already in scientific
 * notation, empty, or mid-typing is returned unchanged.
 */
const toScientificNotation = (value: any): string => {
  const sanitized = sanitizeScientificInput(value ?? "");
  if (isIncomplete(sanitized) || /e/i.test(sanitized)) return sanitized;

  const isNegative = sanitized.startsWith("-");
  const [intPart = "", fracPart = ""] = sanitized.replace("-", "").split(".");
  const digits = intPart + fracPart;
  const firstSignificantIndex = digits.search(/[1-9]/);
  if (firstSignificantIndex === -1) return "0";

  const mantissaDigits = digits.slice(firstSignificantIndex).replace(/0+$/, "") || "0";
  const mantissa =
    mantissaDigits.length > 1 ? `${mantissaDigits[0]}.${mantissaDigits.slice(1)}` : mantissaDigits;
  const exponent = intPart.length - firstSignificantIndex - 1;

  return `${isNegative ? "-" : ""}${mantissa}e${exponent >= 0 ? "+" : "-"}${Math.abs(exponent)}`;
};

/**
 * Decides how a settled (non-focused) value should read: most values are
 * clearer in plain decimal, so scientific notation only kicks in once the
 * fractional part needs more digits than `decimalThreshold` to write out
 * (e.g. `0.0000025` at the default threshold of 2) — a whole number or a
 * value with few decimal places (`100`, `0.25`) stays plain. Values already
 * between 1 and 10 (e.g. `3.14159`) also stay plain: scientific notation
 * would just tack on a no-op `e+0` without saving any digits.
 */
const formatSettledValue = (value: any, decimalThreshold: number): string => {
  const sanitized = sanitizeScientificInput(value ?? "");
  if (isIncomplete(sanitized)) return sanitized;

  const plain = /e/i.test(sanitized) ? toPlainDecimalString(sanitized) : sanitized;
  const fractionalDigits = plain.includes(".") ? plain.split(".")[1].length : 0;
  if (fractionalDigits <= decimalThreshold) return plain;

  const scientific = toScientificNotation(plain);
  return scientific.endsWith("e+0") ? plain : scientific;
};

interface ScientificAmountFieldValue {
  value: any;
  onChange: (value: string) => void;
  onBlur: () => void;
}

/**
 * The actual text box. Split out so it can hold its own focus state:
 * while focused it shows exactly what's being typed (any notation), and on
 * blur it commits a plain-decimal value to the form (what gets saved) while
 * switching its own display over to `formatSettledValue`'s call on whether
 * that's clearer as scientific notation or left as plain decimal.
 */
function ScientificAmountInput({
  field,
  placeholder,
  disabled,
  className,
  decimalThreshold
}: {
  field: ScientificAmountFieldValue;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  decimalThreshold: number;
}) {
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    field.onChange(sanitizeScientificInput(e.target.value));
  };

  const handleFocus = () => setIsFocused(true);

  const handleBlur = () => {
    const currentValue = sanitizeScientificInput(field.value ?? "");
    if (!isIncomplete(currentValue)) {
      field.onChange(toPlainDecimalString(currentValue));
    }
    setIsFocused(false);
    field.onBlur();
  };

  const displayValue = isFocused
    ? sanitizeScientificInput(field.value ?? "")
    : formatSettledValue(field.value ?? "", decimalThreshold);

  return (
    <Input
      type="text"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
    />
  );
}

export function FormScientificUnitInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TUnitName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  required = false,
  placeholder = "0",
  errorMessage,
  min,
  max,
  validate,
  unitType,
  unitOptions,
  unitName,
  unitRequired = false,
  unitErrorMessage,
  disabled = false,
  decimalThreshold = 2
}: FormScientificUnitInputProps<TFieldValues, TName, TUnitName>) {
  // Build validation rules
  const rules: any = {};

  if (required) {
    rules.required = {
      value: true,
      message: typeof required === "string" ? required : errorMessage || `${label} is required`
    };

    rules.validate = {
      ...rules.validate,
      notEmpty: (value: any) => {
        if (isEmpty(value)) {
          return errorMessage || `${label} is required`;
        }
        return true;
      }
    };
  }

  // A value that's still being typed (e.g. "5e-") is neither valid nor
  // invalid yet, so every check below skips it instead of flashing an error.
  rules.validate = {
    ...rules.validate,
    validScientificNumber: (value: any) => {
      if (isIncomplete(value)) return true;
      return Number.isFinite(Number(value)) || errorMessage || `${label} must be a valid number`;
    }
  };

  if (min !== undefined) {
    rules.validate = {
      ...rules.validate,
      minValue: (value: any) => {
        if (isIncomplete(value)) return true;
        const numValue = Number(value);
        const minValue = typeof min === "number" ? min : min.value;
        const minMessage = typeof min === "number" ? `${label} must be at least ${min}` : min.message;
        return numValue >= minValue || minMessage;
      }
    };
  }

  if (max !== undefined) {
    rules.validate = {
      ...rules.validate,
      maxValue: (value: any) => {
        if (isIncomplete(value)) return true;
        const numValue = Number(value);
        const maxValue = typeof max === "number" ? max : max.value;
        const maxMessage = typeof max === "number" ? `${label} must be at most ${max}` : max.message;
        return numValue <= maxValue || maxMessage;
      }
    };
  }

  if (validate) {
    const wrappedValidate =
      typeof validate === "function"
        ? { custom: (value: any) => (isEmpty(value) ? true : validate(value)) }
        : Object.entries(validate).reduce((acc, [key, fn]) => {
            acc[key] = (value: any) => (isEmpty(value) ? true : fn(value));
            return acc;
          }, {} as Record<string, (value: any) => boolean | string>);

    rules.validate = {
      ...rules.validate,
      ...wrappedValidate
    };
  }

  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => {
        return (
          <FormItem>
            <FormLabel>
              {label}
              {required && String(label ?? "").trim() !== "" && (
                <span className="tw-text-destructive tw-ml-0.5">*</span>
              )}
            </FormLabel>
            <div className="tw-relative tw-flex">
              <FormControl>
                <ScientificAmountInput
                  field={field}
                  placeholder={placeholder}
                  disabled={disabled}
                  decimalThreshold={decimalThreshold}
                  className={cn(
                    "tw-text-foreground !tw-pl-2",
                    unitOptions || unitType ? "tw-pr-2 tw-rounded-r-none tw-border-r-0" : ""
                  )}
                />
              </FormControl>

              {/* Dynamic unit selector */}
              {unitOptions && unitName && (
                <FormField
                  control={control}
                  name={unitName}
                  rules={{
                    required: unitRequired
                      ? {
                          value: true,
                          message: unitErrorMessage || "Unit is required"
                        }
                      : false
                  }}
                  render={({ field: unitField }) => (
                    <Select
                      value={String(unitField.value)}
                      disabled={disabled}
                      onValueChange={value => {
                        unitField.onChange(value);
                        field.onBlur();
                      }}
                    >
                      <FormControl>
                        <SelectTrigger className="!tw-h-10 tw-min-w-20 tw-w-auto tw-rounded-l-none tw-border-l tw-bg-muted tw-text-muted-foreground tw-text-xs tw-font-medium tw-shrink-0">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {unitOptions.map(option => (
                          <SelectItem key={option.key || option.value} value={String(option.value)}>
                            {option.label || option.text}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              )}

              {/* Static unit display */}
              {unitType && !unitOptions && (
                <div className="tw-flex tw-items-center tw-justify-center tw-w-24 tw-rounded-r-md tw-border tw-border-l tw-bg-muted tw-px-2 tw-text-xs tw-font-medium tw-text-muted-foreground tw-shrink-0">
                  {unitType}
                </div>
              )}
            </div>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
